import type { DomChangeEvent, TreeNode, TreeSnapshot } from "../DomManagement/Types.ts";
import type { DomSession } from "../DomManagement/DomSession.ts";

// ─── IframeBridge ─────────────────────────────────────────────────────────────
//
// Subscribes to DomSession change events and applies the minimal DOM mutations
// to a live iframe document. This is the only place that touches the real DOM.
//
// Responsibilities:
//   - loadHTML / loadSnapshot  — initial render into the iframe
//   - nodeAdded / nodeRemoved / nodeMoved  — structural mutations
//   - attribute / class / text / tagName / comment updates
//   - builder styles (regenerates a single <style id="rd-builder-styles">)
//   - custom inline styles
//   - @font-face injection via per-font <style> elements
//
// What IframeBridge does NOT do:
//   - No networking — it only reacts to DomSession events
//   - No undo/redo — that lives in UndoStack via DomSession
//   - No patch creation — the Builder layer calls session.capture()

export class IframeBridge {
    private session: DomSession;
    private doc: Document;

    // Maps every tree node ID to its corresponding live DOM node.
    // Populated on load and kept in sync through change events.
    private readonly nodeMap: Map<string, Node> = new Map();

    // <style id="rd-builder-styles"> injected into the iframe <head>.
    // Regenerated wholesale on every builderStyleSet / builderStyleRemoved event.
    private builderStyleEl: HTMLStyleElement | null = null;

    // Width (in px) of the "anchor" / base device — the breakpoint that has no
    // media query and cascades in both directions. Breakpoints wider than this
    // are compiled as `min-width` (ascending cascade); narrower ones as
    // `max-width` (descending cascade). Defaults to "unknown", in which case
    // every non-base breakpoint falls back to the legacy max-width-only
    // behaviour. The Builder layer wires this up (it owns the device list),
    // keeping IframeBridge decoupled from any Builder-specific device manager.
    public getBaseWidth: () => number = () => -1;

    // While true, all change events are silently discarded.
    // Used during loadHTML / loadSnapshot to prevent double-applying the initial render.
    private suppressEvents = false;

    constructor(session: DomSession, iframeDocument: Document) {
        this.session = session;
        this.doc = iframeDocument;
        this.nodeMap.set(session.tree.root.id, iframeDocument);
        session.tree.on("change", this.handleChange);
    }

    public destroy(): void {
        this.session.tree.off("change", this.handleChange);
        this.nodeMap.clear();
        this.builderStyleEl = null;
    }

    // Switches the bridge to a new session without destroying it.
    public rewire(session: DomSession): void {
        this.session.tree.off("change", this.handleChange);
        this.session = session;
        this.nodeMap.clear();
        this.nodeMap.set(session.tree.root.id, this.doc);
        session.tree.on("change", this.handleChange);
    }

    // ─── Initial load ─────────────────────────────────────────────────────────

    /**
     * Writes raw HTML to the iframe and synchronises the tree.
     * Use this instead of calling session.loadFromHTML() directly.
     */
    public loadHTML(html: string): void {
        this.suppressEvents = true;
        this.builderStyleEl = null;
        window.dispatchEvent(new CustomEvent("canvas.willWrite"));
        this.doc.open();
        this.doc.write(html);
        this.doc.close();
        // Populate the tree. Events fire but are suppressed above.
        this.session.loadFromHTML(html);
        this.rebuildNodeMap();
        this.suppressEvents = false;
        // Re-inject builder styles that survived the snapshot.
        this.rebuildBuilderStyles();
    }
    /**
     * Called after the iframe document has been loaded via iframe.src.
     * Stamps data-rd-id on every element that lacks one, parses the live DOM
     * into the tree, and rebuilds the nodeMap — no doc.write, no regex.
     *
     * Must be called AFTER iframe.onload so contentDocument is accessible.
     */
    public syncFromDOM(newDoc: Document): void {
        this.suppressEvents = true;
        this.doc = newDoc;
        this.builderStyleEl = null;
        this.nodeMap.clear();
        this.nodeMap.set(this.session.tree.root.id, newDoc);

        // Stamp data-rd-id on every element before parsing so HtmlParser
        // reuses the same IDs — keeping the tree in sync with the live DOM.
        stampMissingIds(newDoc.documentElement);

        // Parse the stamped live DOM into the tree.
        // loadFromElement suppresses its own change events internally.
        this.session.loadFromElement(newDoc.documentElement);

        this.rebuildNodeMap();
        this.suppressEvents = false;
        this.rebuildBuilderStyles();
    }

    /**
     * Loads a snapshot into the tree and renders it to the iframe.
     * Use this when the server sends a full page snapshot on connect / reconnect.
     */
    public loadSnapshot(snapshot: TreeSnapshot): void {
        this.suppressEvents = true;
        this.builderStyleEl = null;
        this.session.loadSnapshot(snapshot);
        const html = this.serializeTreeToHTML();
        window.dispatchEvent(new CustomEvent("canvas.willWrite"));
        this.doc.open();
        this.doc.write(html);
        this.doc.close();
        this.rebuildNodeMap();
        this.suppressEvents = false;
        this.rebuildBuilderStyles();
    }

    // ─── Node map ─────────────────────────────────────────────────────────────

    private rebuildNodeMap(): void {
        this.nodeMap.clear();
        this.nodeMap.set(this.session.tree.root.id, this.doc);
        this.indexTree(this.session.tree.root, this.doc);
    }

    /**
     * Recursively walks the tree alongside the live DOM and populates nodeMap.
     * Elements are matched by their data-rd-id attribute.
     * Text / comment nodes are matched positionally within each parent.
     */
    private indexTree(treeNode: TreeNode, domNode: Node): void {
        if (treeNode.type !== "element" && treeNode.type !== "document") return;

        const domParent = domNode as Document | Element;
        const domChildren = Array.from(domParent.childNodes);
        // Positional cursor for text/comment nodes within this parent.
        let nonElemIdx = 0;

        for (const treeChild of treeNode.children) {
            if (treeChild.type === "element") {
                const domEl = this.doc.querySelector(
                    `[data-rd-id="${CSS.escape(treeChild.id)}"]`,
                );
                if (domEl) {
                    this.nodeMap.set(treeChild.id, domEl);
                    this.indexTree(treeChild, domEl);
                }
            } else if (treeChild.type === "text" || treeChild.type === "comment") {
                const targetType =
                    treeChild.type === "text" ? Node.TEXT_NODE : Node.COMMENT_NODE;
                // Advance past unmatched DOM nodes until we find one of the right type.
                while (
                    nonElemIdx < domChildren.length &&
                    domChildren[nonElemIdx].nodeType !== targetType
                ) {
                    nonElemIdx++;
                }
                if (nonElemIdx < domChildren.length) {
                    this.nodeMap.set(treeChild.id, domChildren[nonElemIdx]);
                    nonElemIdx++;
                }
            }
        }
    }

    // ─── HTML serializer (snapshot → string) ─────────────────────────────────

    private serializeTreeToHTML(): string {
        return this.session.tree.root.children
            .map(child => this.serializeNode(child))
            .join("");
    }

    private serializeNode(node: TreeNode): string {
        if (node.type === "text") {
            return this.escapeText(node.text?.value ?? "");
        }
        if (node.type === "comment") {
            return `<!--${node.comment?.value ?? ""}-->`;
        }
        if (node.type === "document") {
            return node.children.map(c => this.serializeNode(c)).join("");
        }

        const tag = node.tagName?.value || "div";
        const attrs = this.serializeAttributes(node);
        const children = node.children.map(c => this.serializeNode(c)).join("");

        if (VOID_TAGS.has(tag)) {
            return `<${tag}${attrs}>`;
        }
        return `<${tag}${attrs}>${children}</${tag}>`;
    }

    private serializeAttributes(node: TreeNode): string {
        let result = ` data-rd-id="${node.id}"`;

        const activeClasses: string[] = [];
        for (const [cls, versioned] of node.classes) {
            if (versioned.value) activeClasses.push(cls);
        }
        if (activeClasses.length > 0) {
            result += ` class="${activeClasses.map(c => this.escapeAttr(c)).join(" ")}"`;
        }

        for (const [name, versioned] of node.attributes) {
            if (name === "data-rd-id" || name === "class") continue;
            if (versioned.value === null) continue; // tombstone
            result += ` ${name}="${this.escapeAttr(versioned.value ?? "")}"`;
        }

        return result;
    }

    private escapeText(text: string): string {
        return text
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
    }

    private escapeAttr(value: string): string {
        return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
    }

    // ─── Change event dispatcher ──────────────────────────────────────────────

    private readonly handleChange = (event: DomChangeEvent): void => {
        if (this.suppressEvents || this.session.bridgeSuppressed) return;

        switch (event.type) {
            case "nodeAdded":             this.onNodeAdded(event);            break;
            case "nodeRemoved":           this.onNodeRemoved(event);          break;
            case "nodeMoved":             this.onNodeMoved(event);            break;
            case "nodePositionConfirmed": this.onPositionConfirmed(event);    break;
            case "attributeSet":          this.onAttributeSet(event);         break;
            case "attributeRemoved":      this.onAttributeRemoved(event);     break;
            case "classAdded":            this.onClassAdded(event);           break;
            case "classRemoved":          this.onClassRemoved(event);         break;
            case "textChanged":           this.onTextChanged(event);          break;
            case "tagNameChanged":        this.onTagNameChanged(event);       break;
            case "commentChanged":        this.onCommentChanged(event);       break;
            case "builderStyleSet":
            case "builderStyleRemoved":   this.rebuildBuilderStyles();        break;
            case "customStyleChanged":    this.onCustomStyleChanged(event);   break;
            case "fontAdded":             this.onFontAdded(event);            break;
            case "fontRemoved":           this.onFontRemoved(event);          break;
        }
    };

    // ─── Structural handlers ──────────────────────────────────────────────────

    private onNodeAdded(event: DomChangeEvent): void {
        const node = event.addedNode!;
        const parentDomNode = this.doc.querySelector(`[data-rd-id="${CSS.escape(event.addedToParentId!)}"]`)
                           ?? this.nodeMap.get(event.addedToParentId!);
        if (!parentDomNode) return;

        const newDomNode = this.createDomNode(node);
        const domParent = parentDomNode as Document | Element;

        if (event.siblingBeforeId) {
            const before = this.doc.querySelector(`[data-rd-id="${CSS.escape(event.siblingBeforeId)}"]`)
                        ?? this.nodeMap.get(event.siblingBeforeId);
            if (before?.parentNode) {
                before.parentNode.insertBefore(newDomNode, before.nextSibling);
                return;
            }
        }

        if (event.siblingAfterId) {
            const after = this.doc.querySelector(`[data-rd-id="${CSS.escape(event.siblingAfterId)}"]`)
                       ?? this.nodeMap.get(event.siblingAfterId);
            if (after?.parentNode) {
                after.parentNode.insertBefore(newDomNode, after);
                return;
            }
        }

        domParent.appendChild(newDomNode);
    }

    /**
     * Creates a DOM node (and all its descendants) from a TreeNode.
     * Registers every created node in nodeMap.
     */
    private createDomNode(node: TreeNode): Node {
        if (node.type === "text") {
            const tn = this.doc.createTextNode(node.text?.value ?? "");
            this.nodeMap.set(node.id, tn);
            return tn;
        }

        if (node.type === "comment") {
            const cn = this.doc.createComment(node.comment?.value ?? "");
            this.nodeMap.set(node.id, cn);
            return cn;
        }

        const tag = node.tagName?.value || "div";
        const el = this.doc.createElement(tag);
        el.setAttribute("data-rd-id", node.id);

        for (const [className, versioned] of node.classes) {
            if (versioned.value) el.classList.add(className);
        }

        for (const [name, versioned] of node.attributes) {
            if (name === "class") continue; // handled above
            if (versioned.value === null) continue; // tombstone
            el.setAttribute(name, versioned.value ?? "");
        }

        this.nodeMap.set(node.id, el);

        for (const child of node.children) {
            el.appendChild(this.createDomNode(child));
        }

        return el;
    }

    private onNodeRemoved(event: DomChangeEvent): void {
       
        // Use querySelector so we always find the live DOM element, not a stale nodeMap entry.
        const domNode = this.doc.querySelector(`[data-rd-id="${CSS.escape(event.nodeId)}"]`)
                     ?? this.nodeMap.get(event.nodeId); // fallback for text/comment nodes
        this.cleanupSubtree(event.nodeId);
        if (domNode) domNode.parentNode?.removeChild(domNode);
        this.rebuildBuilderStyles();
    }

    /**
     * Recursively removes all nodeMap entries that correspond to domNode
     * or any of its descendants.
     */
    private cleanupSubtree(nodeIdOrDomNode: string | Node): void {
        if (typeof nodeIdOrDomNode === "string") {
            // Called with just an ID: clean by querying the live DOM.
            const el = this.doc.querySelector(`[data-rd-id="${CSS.escape(nodeIdOrDomNode)}"]`);
            if (el) {
                this.cleanupSubtree(el);
            } else {
                this.nodeMap.delete(nodeIdOrDomNode);
            }
            return;
        }
        const domNode = nodeIdOrDomNode;
        if (domNode.nodeType === Node.ELEMENT_NODE) {
            const id = (domNode as Element).getAttribute("data-rd-id");
            if (id) this.nodeMap.delete(id);
            for (const child of Array.from(domNode.childNodes)) {
                this.cleanupSubtree(child);
            }
        } else {
            // Text / comment: linear scan.
            for (const [id, node] of this.nodeMap) {
                if (node === domNode) {
                    this.nodeMap.delete(id);
                    return;
                }
            }
        }
    }

    private onNodeMoved(event: DomChangeEvent): void {
        const domNode = this.doc.querySelector(`[data-rd-id="${CSS.escape(event.nodeId)}"]`)
                     ?? this.nodeMap.get(event.nodeId);
        const newParentDom = this.doc.querySelector(`[data-rd-id="${CSS.escape(event.newParentId!)}"]`)
                          ?? this.nodeMap.get(event.newParentId!);
        if (!domNode || !newParentDom) return;

        const treeParent = this.session.tree.findNodeById(event.newParentId!);
        if (!treeParent) {
            (newParentDom as Element | Document).appendChild(domNode);
            return;
        }

        const movedIdx = treeParent.children.findIndex(c => c.id === event.nodeId);

        // Find the first next sibling in tree order that exists in the DOM,
        // then insert directly before it into newParentDom.
        // This avoids relying on prevSibling.parentNode which may differ from newParentDom.
        let refNode: Node | null = null;
        for (let i = movedIdx + 1; i < treeParent.children.length; i++) {
            const sib = treeParent.children[i];
            const sibDom = this.doc.querySelector(`[data-rd-id="${CSS.escape(sib.id)}"]`)
                        ?? this.nodeMap.get(sib.id);
            if (sibDom) { refNode = sibDom; break; }
        }

        (newParentDom as Element | Document).insertBefore(domNode, refNode);
    }

    private onPositionConfirmed(event: DomChangeEvent): void {
        // The tree has already re-sorted its children array by LexoRank.
        // Re-append children in that order to reflect the server-confirmed order in the DOM.
        const treeNode = this.session.tree.findNodeById(event.nodeId);
        if (!treeNode?.parentId) return;

        const treeParent = this.session.tree.findNodeById(treeNode.parentId);
        if (!treeParent) return;

        const domParent = this.nodeMap.get(treeParent.id);
        if (!domParent) return;

        // Walk the DOM child list and the tree child list together.
        // If every tree child already appears in the correct relative order in the
        // DOM (ignoring non-tree nodes such as injected <style> elements), the sort
        // is a no-op — skip it entirely to avoid unnecessary DOM moves and repaints.
        // This is the common case in single-user editing.
        const domChildNodes = Array.from((domParent as Element | Document).childNodes);
        let domCursor = 0;
        let outOfOrder = false;
        for (const treeChild of treeParent.children) {
            const childDom = this.nodeMap.get(treeChild.id);
            if (!childDom) continue;
            // Advance past DOM nodes that are not tracked in nodeMap (e.g. <style> tags).
            while (domCursor < domChildNodes.length && domChildNodes[domCursor] !== childDom) {
                domCursor++;
            }
            if (domCursor >= domChildNodes.length) {
                outOfOrder = true;
                break;
            }
            domCursor++;
        }

        if (!outOfOrder) return;

        for (const child of treeParent.children) {
            const childDom = this.nodeMap.get(child.id);
            if (childDom) {
                (domParent as Element | Document).appendChild(childDom);
            }
        }
    }

    // ─── Property handlers ────────────────────────────────────────────────────

    private onAttributeSet(event: DomChangeEvent): void {
        const el = this.nodeMap.get(event.nodeId) as Element | undefined;
        if (!el || el.nodeType !== Node.ELEMENT_NODE) return;
        el.setAttribute(event.attributeName!, event.attributeValue ?? "");
    }

    private onAttributeRemoved(event: DomChangeEvent): void {
        const el = this.nodeMap.get(event.nodeId) as Element | undefined;
        if (!el || el.nodeType !== Node.ELEMENT_NODE) return;
        el.removeAttribute(event.attributeName!);
    }

    private onClassAdded(event: DomChangeEvent): void {
        const el = this.nodeMap.get(event.nodeId) as Element | undefined;
        if (!el || el.nodeType !== Node.ELEMENT_NODE) return;
        el.classList.add(event.className!);
    }

    private onClassRemoved(event: DomChangeEvent): void {
        const el = this.nodeMap.get(event.nodeId) as Element | undefined;
        if (!el || el.nodeType !== Node.ELEMENT_NODE) return;
        el.classList.remove(event.className!);
    }

    private onTextChanged(event: DomChangeEvent): void {
        const tn = this.nodeMap.get(event.nodeId) as Text | undefined;
        if (!tn) return;
        tn.nodeValue = event.textValue ?? "";
    }

    private onCommentChanged(event: DomChangeEvent): void {
        const cn = this.nodeMap.get(event.nodeId) as Comment | undefined;
        if (!cn) return;
        cn.nodeValue = event.commentValue ?? "";
    }

    private onTagNameChanged(event: DomChangeEvent): void {
        const oldEl = this.nodeMap.get(event.nodeId) as Element | undefined;
        if (!oldEl || oldEl.nodeType !== Node.ELEMENT_NODE) return;

        const newEl = this.doc.createElement(event.tagName!);

        // Copy all attributes, including data-rd-id.
        for (const attr of Array.from(oldEl.attributes)) {
            newEl.setAttribute(attr.name, attr.value);
        }

        // Move all child nodes.
        while (oldEl.firstChild) {
            newEl.appendChild(oldEl.firstChild);
        }

        oldEl.parentNode?.replaceChild(newEl, oldEl);
        this.nodeMap.set(event.nodeId, newEl);
    }

    private onCustomStyleChanged(event: DomChangeEvent): void {
        const el = this.nodeMap.get(event.nodeId) as HTMLElement | undefined;
        if (!el || el.nodeType !== Node.ELEMENT_NODE) return;
        el.style.setProperty(event.customStyleKey!, event.customStyleValue ?? "");
    }

    // ─── Builder styles ───────────────────────────────────────────────────────

    /**
     * Rebuilds the entire builder <style> element from scratch.
     * Called on every builderStyleSet / builderStyleRemoved event.
     * Generates rules in the form:
     *   [data-rd-id="<id>"]<pseudo> { <property>: <value>; }
     */
    private rebuildBuilderStyles(): void {
        const baseWidth = this.getBaseWidth();
        // Each entry carries a sort key so the emitted <style> text has the
        // correct cascade order: base first, then the below-base group in
        // descending width order (narrower = later = higher precedence), then
        // the above-base group in ascending width order (wider = later =
        // higher precedence). See the `getBaseWidth` doc comment above.
        const entries: { key: number; css: string }[] = [];

        for (const [nodeId, styles] of this.session.tree.builderStyles) {
            for (const style of styles) {
                if (style.value === undefined) continue; // tombstone

                // Pseudo: stored without `:` prefix (e.g. "hover") — add it for CSS.
                const pseudoPart = style.pseudo ? `:${style.pseudo}` : "";
                const selector = `[data-rd-id="${nodeId}"]${pseudoPart}`;
                const declaration = `${style.property}: ${style.value} !important;`;

                // Wrap in media query if a breakpoint is set.
                const bp = style.media ? parseFloat(style.media) : NaN;
                const isAboveBase = !isNaN(bp) && baseWidth > 0 && bp > baseWidth;
                let sortKey = -Infinity;
                let mediaWrap: string | null = null;
                if (style.media) {
                    if (!isNaN(bp)) {
                        mediaWrap = isAboveBase ? `(min-width: ${bp - 0.98}px)` : `(max-width: ${bp + 0.98}px)`;
                        sortKey = isAboveBase ? bp : -bp;
                    } else {
                        mediaWrap = style.media;
                        sortKey = 0;
                    }
                }

                // Wrap in theme condition if a theme is set.
                // Mirrors StylesheetManager: dark → @media prefers-color-scheme AND [s-theme] attr.
                let rule: string;
                if (style.theme) {
                    const darkCondition = `(prefers-color-scheme: ${style.theme})`;
                    // Combine dark + breakpoint into a single @media with `and` (no nesting).
                    const mediaCondition = mediaWrap
                        ? `@media ${darkCondition} and ${mediaWrap}`
                        : `@media ${darkCondition}`;
                    const innerRule  = `${selector} { ${declaration} }`;
                    const attrSelector = `[s-theme="${style.theme}"] ${selector}, [s-theme="${style.theme}"]${selector}`;
                    const attrRule = mediaWrap
                        ? `@media ${mediaWrap} { ${attrSelector} { ${declaration} } }`
                        : `${attrSelector} { ${declaration} }`;
                    rule = `${mediaCondition} {\n    ${innerRule}\n}\n${attrRule}`;
                } else {
                    rule = mediaWrap
                        ? `@media ${mediaWrap} { ${selector} { ${declaration} } }`
                        : `${selector} { ${declaration} }`;
                }

                entries.push({ key: sortKey, css: rule });
            }
        }

        entries.sort((a, b) => a.key - b.key);

        const head = this.doc.head ?? this.doc.querySelector("head");
        if (!head) return;

        if (!this.builderStyleEl) {
            this.builderStyleEl = this.doc.createElement("style");
            this.builderStyleEl.id = "rd-builder-styles";
            head.appendChild(this.builderStyleEl);
        }

        this.builderStyleEl.textContent = entries.map((e) => e.css).join("\n");
    }

    // ─── Fonts ────────────────────────────────────────────────────────────────

    private onFontAdded(event: DomChangeEvent): void {
        const font = this.session.tree.fonts.get(event.fontId!);
        if (!font) return;
        const head = this.doc.head ?? this.doc.querySelector("head");
        if (!head) return;

        const styleId = `rd-font-${font.id}`;
        if (this.doc.getElementById(styleId)) return;

        // font.contentUrl holds the full @font-face CSS block.
        const styleEl = this.doc.createElement("style");
        styleEl.id = styleId;
        styleEl.textContent = font.contentUrl;
        head.appendChild(styleEl);
    }

    private onFontRemoved(event: DomChangeEvent): void {
        this.doc.getElementById(`rd-font-${event.fontId}`)?.remove();
    }
}

// ─── DOM helpers ────────────────────────────────────────────────────────────────

/**
 * Recursively stamps data-rd-id on every element that does not already have one.
 * Called before parseElement so HtmlParser reuses the generated IDs.
 */
function stampMissingIds(el: Element): void {
    if (!el.hasAttribute("data-rd-id")) {
        el.setAttribute("data-rd-id", crypto.randomUUID());
    }
    for (const child of Array.from(el.children)) {
        stampMissingIds(child);
    }
}

// ─── Void element tag set ─────────────────────────────────────────────────────

const VOID_TAGS = new Set([
    "area", "base", "br", "col", "embed", "hr", "img", "input",
    "link", "meta", "param", "source", "track", "wbr",
]);
